import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ".././patients.css";
import { useParams, useOutletContext } from "react-router-dom";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import TiptapEditor from "../../../components/layout/tiptapEditor/TiptapEditor";
import TiptapViewer from "../../../components/layout/tiptapViewer/TiptapViewer";
import SelectModal from "../../../components/layout/selectModal/SelectModal";
import { Button } from "../../../components/ui";
import "./PatientPlan.css";
import "./patientListAppointments.css";
import "./patientRecommendations.css";
import LogoText from "../../../media/logo_text.webp";
import PlanTabs from "./PlanTabs";
import { IoDocumentTextOutline, IoChevronDownOutline } from "react-icons/io5";
import Toast from "../../../components/layout/toast/Toast";

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const normalizeDisplayValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

const getTemplateDisplayName = (template, t) =>
  template?.title?.value ||
  template?.name?.value ||
  t("recommendations.templateFallback", { id: template?.id?.value ?? "—" });

function formatDateShortHeader(dateString) {
  if (
    !dateString ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "—";
  }
  const [datePart] = dateString.split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
}

function getFirstPlanOrActualDateFromAdmissions(admissions) {
  if (!admissions) return null;

  const isValidDate = (dateString) =>
    dateString &&
    dateString !== "0000-00-00 00:00" &&
    dateString !== "0000-00-00 00:00:00" &&
    !isNaN(new Date(dateString).getTime());

  let firstPlanDate = null;
  let firstActualDate = null;

  Object.values(admissions).forEach((adm) => {
    const plan = adm?.date_plan?.value;
    const actual = adm?.date?.value;

    if (isValidDate(plan)) {
      if (!firstPlanDate || new Date(plan) < new Date(firstPlanDate)) {
        firstPlanDate = plan;
      }
    }

    if (isValidDate(actual)) {
      if (!firstActualDate || new Date(actual) < new Date(firstActualDate)) {
        firstActualDate = actual;
      }
    }
  });

  if (!firstPlanDate && !firstActualDate) return null;
  if (!firstPlanDate) return firstActualDate;
  if (!firstActualDate) return firstPlanDate;

  return new Date(firstPlanDate) < new Date(firstActualDate)
    ? firstPlanDate
    : firstActualDate;
}

function PatientRecommendations() {
  const { t } = useTranslation("patient");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { patientId, planId } = useParams();
  const { planGroupsSlotEl } = useOutletContext() || {};
  // Контент секции рендерится в отдельную карточку-слот страницы
  // (вне верхнего блока с табами), как в самом плане лечения.
  const renderSection = (node) =>
    planGroupsSlotEl ? createPortal(node, planGroupsSlotEl) : node;

  const currentPlanId = Number(planId) || 0;

  const [recommendationsList, setRecommendationsList] = useState([]);
  // Редактирование — в модальном окне. null — окно закрыто,
  // иначе { id, title, description, originalTitle, originalDescription }.
  const [editItem, setEditItem] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Уведомления — единым тостом вместо модальных окон.
  const [toast, setToast] = useState({});
  const notify = (title, status) => {
    if (!title) return;
    setToast({ id: Date.now(), title, status: status || "info" });
  };

  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateType, setTemplateType] = useState("common");

  // Раскрывающийся список у кнопки «Добавить»: пустая рекомендация или шаблон.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef(null);

  useEffect(() => {
    if (!addMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (!addMenuRef.current?.contains(event.target)) setAddMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [addMenuOpen]);

  const [appointmentsPrint, setAppointmentsPrint] = useState(null);

  useEffect(() => {
    loadRecommendations();
  }, [patientId, planId]);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const apts = await GetData({
          module: "patient",
          block: "list_appointments",
          id_patient: patientId,
        });
        if (!cancelled) {
          setAppointmentsPrint(apts);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const showAlert = (message) => notify(message, "error");

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await GetData({
        module: "patient",
        block: "recommendations",
        id_patient: patientId,
        id_plan: currentPlanId,
        id_recommendation: 0,
      });

      const list = Array.isArray(data?.result)
        ? data.result
        : data?.result?.title?.value || data?.result?.description?.value
          ? [data.result]
          : [];
      setRecommendationsList(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const requestRecommendations = async ({
    action = "",
    id_template = "",
    id_recommendation = 0,
    payload = {},
  } = {}) => {
    return GetData({
      module: "patient",
      block: "recommendations",
      id_patient: patientId,
      id_plan: currentPlanId,
      id_recommendation: id_recommendation || 0,
      action,
      id_template,
      ...payload,
    });
  };

  const handleCloseTemplatesModal = () => {
    setShowTemplatesModal(false);
    setSelectedTemplate("");
    setTemplates([]);
  };

  const loadTemplates = async (source = templateType) => {
    setTemplatesLoading(true);
    setSelectedTemplate("");
    try {
      const response = await requestRecommendations({
        action: "get_recommendation_templates",
        id_recommendation: 0,
        payload: {
          template_source: source === "my" ? "my" : "common",
        },
      });
      setTemplates(response.templates || []);
    } catch (err) {
      setTemplates([]);
      showAlert(t("recommendations.alert.loadTemplatesFailed", { message: err.message }));
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleAddFromTemplate = async () => {
    setTemplateType("common");
    setShowTemplatesModal(true);
    await loadTemplates("common");
  };

  // «Пустая рекомендация» — открываем модалку в режиме создания;
  // запись создаётся только после нажатия «Сохранить».
  const handleAddEmpty = () => {
    setEditItem({
      id: 0,
      isNew: true,
      title: "",
      description: "",
      originalTitle: "",
      originalDescription: "",
    });
  };

  const handleTemplateTypeChange = async (nextType) => {
    if (nextType === templateType || templatesLoading) return;
    setTemplateType(nextType);
    await loadTemplates(nextType);
  };

  const handleSaveTemplate = async (template) => {
    const templateId = template?.id?.value || selectedTemplate;
    if (!templateId) {
      notify(t("recommendations.alert.selectTemplate"), "info");
      return;
    }

    try {
      setTemplatesLoading(true);

      const data = await requestRecommendations({
        action: "add_template",
        id_template: templateId,
        id_recommendation: 0,
      });

      const serverStatus = data?.status;
      if (!serverStatus || serverStatus.success !== true) {
        notify(
          serverStatus?.message || t("recommendations.alert.applyFailed", {
            message: t("recommendations.alert.noServerResponse"),
          }),
          "error",
        );
        return;
      }

      notify(serverStatus.message || t("recommendations.templateApplied"), "success");

      const selectedTemplateData = templates.find(
        (item) => String(item?.id?.value) === String(templateId),
      );
      await logPatientChange({
        idPatient: patientId,
        idItem: templateId,
        text: buildPatientLogText("patient_recommendations_template_applied", {
          details: `Применен шаблон: ${getTemplateDisplayName(selectedTemplateData, t)} (ID: ${normalizeDisplayValue(templateId)})`,
        }),
      });

      handleCloseTemplatesModal();
      await loadRecommendations();
    } catch (err) {
      notify(
        t("recommendations.alert.applyFailed", { message: err.message }),
        "error",
      );
    } finally {
      setTemplatesLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  //  Редактирование рекомендации — в модальном окне
  // ---------------------------------------------------------------------

  const openEditModal = (item) => {
    const id = Number(item?.id?.value ?? item?.id) || 0;
    if (!id) return;
    const title = item?.title?.value || "";
    const description = item?.description?.value || "";
    setEditItem({
      id,
      title,
      description,
      originalTitle: title,
      originalDescription: description,
    });
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditItem(null);
  };

  const handleEditFieldChange = (field) => (event) => {
    const value =
      typeof event === "string" ? event : event?.target?.value ?? "";
    setEditItem((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    if (!editItem.title.trim() || !String(editItem.description || "").trim()) {
      notify(t("recommendations.alert.fillAll"), "info");
      return;
    }

    setEditSaving(true);
    try {
      const isNew = Boolean(editItem.isNew);
      const data = await requestRecommendations({
        action: isNew ? "add" : "change",
        id_recommendation: isNew ? 0 : editItem.id,
        payload: {
          title: editItem.title,
          description: editItem.description,
        },
      });

      // Результат определяет ТОЛЬКО ответ сервера: нет ответа или
      // status.success === false — показываем ошибку, иначе успех.
      const serverStatus = data?.status;
      if (!serverStatus || serverStatus.success !== true) {
        notify(
          serverStatus?.message || t("recommendations.alert.saveFailed", {
            message: t("recommendations.alert.noServerResponse"),
          }),
          "error",
        );
        return;
      }

      // Новая рекомендация — логируем факт создания и выходим.
      if (isNew) {
        await logPatientChange({
          idPatient: patientId,
          text: buildPatientLogText("patient_recommendations_created", {
            details: `Создана рекомендация: ${editItem.title}`,
          }),
        });

        setEditItem(null);
        notify(serverStatus.message || t("recommendations.created"), "success");
        await loadRecommendations();
        return;
      }

      const previousTitle = editItem.originalTitle;
      const previousDescription = editItem.originalDescription;
      const nextTitle = editItem.title;
      const nextDescription = editItem.description;

      const changes = [];
      if (
        normalizeComparableValue(previousTitle) !==
        normalizeComparableValue(nextTitle)
      ) {
        changes.push({
          label: "Заголовок",
          from: previousTitle,
          to: nextTitle,
        });
      }
      if (
        normalizeComparableValue(previousDescription) !==
        normalizeComparableValue(nextDescription)
      ) {
        changes.push({
          label: "Описание",
          from: `${normalizeComparableValue(previousDescription).length} символов`,
          to: `${normalizeComparableValue(nextDescription).length} символов`,
        });
      }

      await logPatientChange({
        idPatient: patientId,
        text: buildPatientLogText("patient_recommendations_updated", {
          changes,
          details:
            changes.length === 0
              ? "Изменений в рекомендациях не обнаружено"
              : "",
        }),
      });

      setEditItem(null);
      notify(serverStatus.message || t("recommendations.saved"), "success");

      await loadRecommendations();
    } catch (err) {
      notify(
        t("recommendations.alert.saveFailed", { message: err.message }),
        "error",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const targetId = Number(id);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (!window.confirm(t("recommendations.alert.confirmDelete"))) return;

    try {
      const data = await requestRecommendations({
        action: "delete",
        id_recommendation: targetId,
      });

      const serverStatus = data?.status;
      if (!serverStatus || serverStatus.success !== true) {
        notify(
          serverStatus?.message || t("recommendations.alert.deleteFailed", {
            message: t("recommendations.alert.noServerResponse"),
          }),
          "error",
        );
        return;
      }

      notify(serverStatus.message || t("recommendations.deleted"), "success");

      await loadRecommendations();
    } catch (err) {
      notify(
        t("recommendations.alert.deleteFailed", { message: err.message }),
        "error",
      );
    }
  };

  if (loading)
    return <div>{t("recommendations.loading")}</div>;
  if (error)
    return <div>{t("error", { ns: "common", message: error })}</div>;

  const apt = appointmentsPrint?.result;

  // Шапка карточки — общая для списка и просмотра записи.
  const sectionHead = (actions) => (
    <div className="card__head plan-matrix-head plan-card__head">
      <div className="plan-matrix-head__title">
        <div className="card__title plan-matrix-title">
          <IoDocumentTextOutline size={19} aria-hidden="true" />
          {t("recommendations.title")}
        </div>
        <div className="card__sub plan-matrix-sub">
          {t("recommendations.sub")}
        </div>
      </div>
      <div className="plan-matrix-head__switch" />
      <div className="plan-matrix-head__actions">{actions}</div>
    </div>
  );

  return (
    <>
      {toast.title ? (
        <Toast key={toast.id} status={toast.status} title={toast.title} />
      ) : null}
      <div className="patient-recommendations-view">
        <PlanTabs active="recommendations" />
      </div>
      {renderSection(
        <div className="page-block patient-plan-card plan-section-body">
          {sectionHead(
            <div className={`rec-add${addMenuOpen ? " is-open" : ""}`} ref={addMenuRef}>
              <Button
                icon="add"
                onClick={() => setAddMenuOpen((prev) => !prev)}
                variant="primary">
                <span className="rec-add__label">
                  {t("recommendations.add")}
                  <IoChevronDownOutline
                    aria-hidden="true"
                    className="rec-add__chev"
                    size={14}
                  />
                </span>
              </Button>
              {addMenuOpen ? (
                <div className="rec-add__menu" role="menu">
                  <button
                    type="button"
                    className="rec-add__item"
                    role="menuitem"
                    onClick={() => {
                      setAddMenuOpen(false);
                      handleAddEmpty();
                    }}>
                    {t("recommendations.addMenu.empty")}
                  </button>
                  <button
                    type="button"
                    className="rec-add__item"
                    role="menuitem"
                    onClick={() => {
                      setAddMenuOpen(false);
                      handleAddFromTemplate();
                    }}>
                    {t("recommendations.addMenu.fromTemplate")}
                  </button>
                </div>
              ) : null}
            </div>,
          )}

          {recommendationsList.length === 0 ? (
            <div className="pd-empty">{t("recommendations.emptyList")}</div>
          ) : (
            <div className="rec-doc">
              {recommendationsList.map((item) => {
                const id = item?.id?.value;
                return (
                  <section key={id} className="rec-section">
                    <div className="rec-section__title">
                      <span className="rec-section__title-text">
                        {item?.title?.value || "—"}
                      </span>
                      <span className="rec-section__title-actions">
                        <Button
                          icon="edit"
                          onClick={() => openEditModal(item)}
                          size="sm"
                          variant="secondary">
                          {t("recommendations.edit")}
                        </Button>
                        <Button
                          aria-label={t("recommendations.delete")}
                          icon="trash"
                          iconOnly
                          onClick={() => handleDelete(id)}
                          size="sm"
                          title={t("recommendations.delete")}
                          variant="danger"
                        />
                      </span>
                    </div>
                    <div className="rec-section__body">
                      <div className="rec-section__text">
                        <TiptapViewer
                          content={item?.description?.value}
                          emptyText={t("recommendations.emptyDescription")}
                        />
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>,
      )}

      <div className="patient-recommendations-print">
            <div className="patient-info-print">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 12,
                }}>
                <img
                  src={LogoText}
                  alt={t("recommendations.print.logoAlt")}
                  style={{ width: 140, height: 56, objectFit: "contain" }}
                />
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}>
                  <div>{t("recommendations.print.heading")}</div>
                </div>
              </div>
              <div className="info-print-first info-print-first--two-cols">
                <div className="info-item-print">
                  <div>{t("recommendations.print.fioLabel")} {apt?.fio?.value ?? "—"}</div>
                  <div>
                    {t("recommendations.print.birthDateLabel")}{" "}
                    {formatDateShortHeader(apt?.birthDate?.value)}
                  </div>
                  <div>{t("recommendations.print.cardLabel")} {apt?.cart_patient?.value ?? "—"}</div>
                  <div>
                    {t("recommendations.print.treatmentStartLabel")}{" "}
                    {formatDateShortHeader(
                      getFirstPlanOrActualDateFromAdmissions(apt?.admission),
                    )}
                  </div>
                </div>
                <div className="info-item-print">
                  <div>{t("recommendations.print.doctorLabel")} {apt?.doc_name?.value ?? "—"}</div>
                  <div>{t("recommendations.print.signatureLabel")}</div>
                </div>
              </div>
            </div>

            {recommendationsList.map((item) => (
              <div key={item?.id?.value} className="rec-print-item">
                <div className="recommendations-print-title">
                  {item?.title?.value || "—"}
                </div>
                <div className="recommendations-print-body recommendation-item">
                  <TiptapViewer
                    content={item?.description?.value}
                    emptyText={t("recommendations.emptyDescription")}
                  />
                </div>
              </div>
            ))}
          </div>

      {/* Редактирование рекомендации — в модальном окне */}
      <Modal
        show={Boolean(editItem)}
        onHide={closeEditModal}
        centered
        size="lg"
        className="rec-edit-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {editItem?.isNew
              ? t("recommendations.addTitle")
              : t("recommendations.editTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="recommendationTitle" className="mb-3">
            <Form.Label>{t("recommendations.form.titleLabel")}</Form.Label>
            <Form.Control
              type="text"
              value={editItem?.title || ""}
              onChange={handleEditFieldChange("title")}
              placeholder={t("recommendations.form.titlePlaceholder")}
              autoFocus
            />
          </Form.Group>

          <Form.Group controlId="recommendationDescription">
            <Form.Label>{t("recommendations.form.descriptionLabel")}</Form.Label>
            <TiptapEditor
              value={editItem?.description || ""}
              onChange={handleEditFieldChange("description")}
              placeholder={t("recommendations.form.descriptionPlaceholder")}
              minHeight="220px"
            />
            <Form.Text className="text-muted">
              {t("recommendations.form.hint")}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeEditModal} variant="secondary">
            {t("recommendations.cancel")}
          </Button>
          <Button
            loading={editSaving}
            onClick={handleSaveEdit}
            variant="primary">
            {t("recommendations.save")}
          </Button>
        </Modal.Footer>
      </Modal>

      <SelectModal
        show={showTemplatesModal}
        onHide={handleCloseTemplatesModal}
        title={t("recommendations.templates.selectTitle")}
        loading={templatesLoading}
        options={templates}
        value={selectedTemplate}
        onChange={setSelectedTemplate}
        onSelect={handleSaveTemplate}
        optionLabel={(item) =>
          item.title?.value ||
          t("recommendations.templateFallback", { id: item.id?.value ?? "—" })
        }
        optionValue={(item) => String(item.id?.value)}
        confirmButtonText={t("recommendations.templates.confirm")}
        emptyMessage={t("recommendations.templates.empty")}
        loadingMessage={t("recommendations.templates.loading")}
        topContent={
          <div className="template-type-options mb-3">
            <div className="mb-2">
              <label>
                <input
                  type="radio"
                  name="recommendationTemplateType"
                  value="common"
                  checked={templateType === "common"}
                  onChange={() => handleTemplateTypeChange("common")}
                  disabled={templatesLoading}
                />{" "}
                {t("recommendations.templates.common")}
              </label>
            </div>
            <div>
              <label>
                <input
                  type="radio"
                  name="recommendationTemplateType"
                  value="my"
                  checked={templateType === "my"}
                  onChange={() => handleTemplateTypeChange("my")}
                  disabled={templatesLoading}
                />{" "}
                {t("recommendations.templates.my")}
              </label>
            </div>
          </div>
        }
      />
    </>
  );
}

export default PatientRecommendations;
